<template>
  <div class="view-request-logs">
    <!-- Access Control -->
    <hs-empty-state
      v-if="!isSuperAdmin"
      icon="lock"
      title="Access denied"
      description="You need super admin permissions to view request logs."
    />

    <!-- Request Logs Content -->
    <div v-else class="view-request-logs__content">
      <div class="view-request-logs__summary">
        <div class="view-request-logs__summary-item">
          <span>Total Logs</span>
          <strong>{{ pagination.totalItems }}</strong>
        </div>
        <div class="view-request-logs__summary-item">
          <span>Current Page</span>
          <strong>{{ pagination.currentPage }} / {{ pagination.totalPages }}</strong>
        </div>
        <div class="view-request-logs__summary-item">
          <span>Page Size</span>
          <strong>{{ pagination.itemsPerPage }}</strong>
        </div>
      </div>

      <!-- Error State -->
      <hs-alert
        v-if="error"
        class="view-request-logs__alert"
        tone="danger"
        :message="error.message || 'Failed to load request logs'"
      />

      <!-- Request Logs Table -->
      <request-logs-table
        :logs="logs"
        :loading="loading"
        :pagination="pagination"
        :filters="filters"
        @filter-change="handleFilterChange"
        @sort-change="handleSortChange"
        @previous-page="handlePreviousPage"
        @next-page="handleNextPage"
        @items-per-page-change="handleItemsPerPageChange"
        @view-details="handleViewDetails"
        @refresh="handleRefresh"
      />

      <!-- Request Log Details Modal -->
      <request-log-details-modal
        :is-open="showDetailsModal"
        :request-log="selectedRequestLog"
        @close="closeDetailsModal"
      />
    </div>
  </div>
</template>

<script>
import { ref, computed, reactive, watch } from "vue";
import RequestLogsTable from "@/components/admin/RequestLogsTable.vue";
import RequestLogDetailsModal from "@/components/modals/admin/RequestLogDetailsModal.vue";
import { useRequestLogs } from "@/composables/useRequestLogsQuery";
import { mapGetters } from "vuex";
import { useQueryClient } from "@tanstack/vue-query";
import { HsAlert, HsEmptyState } from "@/components/ui";

export default {
  name: "ViewRequestLogs",
  components: {
    HsAlert,
    HsEmptyState,
    RequestLogsTable,
    RequestLogDetailsModal,
  },
  setup() {
    const filters = reactive({});
    const pagination = reactive({
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 50,
    });

    const showDetailsModal = ref(false);
    const selectedRequestLog = ref(null);
    const isRefreshing = ref(false);
    const queryClient = useQueryClient();

    // Build query parameters
    const queryParams = computed(() => {
      const params = {
        page: pagination.currentPage,
        per_page: pagination.itemsPerPage,
        sort: "+created_at", // Use + for descending (newest first) due to backend toggle logic
        ...filters,
      };
      return params;
    });

    const {
      data: logsData,
      isLoading: loading,
      error,
      refetch,
    } = useRequestLogs(queryParams);

    const logs = computed(() => logsData.value?.items || []);

    // Watch for data changes and update pagination
    watch(
      logsData,
      (newData) => {
        if (newData) {
          pagination.currentPage = newData.page || 1;
          pagination.totalPages = newData.pages || 1;
          pagination.totalItems = newData.total || 0;
          // Use the actual per_page from response, but keep our desired default
          pagination.itemsPerPage = newData.per_page || pagination.itemsPerPage;
        }
      },
      { immediate: true }
    );

    const handleFilterChange = (newFilters) => {
      Object.assign(filters, newFilters);
      pagination.currentPage = 1;
    };

    const handleSortChange = (sortValue) => {
      filters.sort = sortValue;
    };

    const handlePreviousPage = () => {
      if (pagination.currentPage > 1) {
        pagination.currentPage--;
      }
    };

    const handleNextPage = () => {
      if (pagination.currentPage < pagination.totalPages) {
        pagination.currentPage++;
      }
    };

    const handleItemsPerPageChange = (itemsPerPage) => {
      pagination.itemsPerPage = itemsPerPage;
      pagination.currentPage = 1;
    };

    const handleViewDetails = (requestLog) => {
      selectedRequestLog.value = requestLog;
      showDetailsModal.value = true;
    };

    const closeDetailsModal = () => {
      showDetailsModal.value = false;
      selectedRequestLog.value = null;
    };

    const handleRefresh = async () => {
      isRefreshing.value = true;
      try {
        // Invalidate the query cache to force a fresh fetch
        await queryClient.invalidateQueries({
          queryKey: ["requestLogs"],
        });
        // Also call refetch as backup
        await refetch();
      } finally {
        // Add a small delay to ensure the loading state is visible
        setTimeout(() => {
          isRefreshing.value = false;
        }, 500);
      }
    };

    return {
      filters,
      pagination,
      logs,
      loading: computed(() => loading.value || isRefreshing.value),
      error,
      showDetailsModal,
      selectedRequestLog,
      handleFilterChange,
      handleSortChange,
      handlePreviousPage,
      handleNextPage,
      handleItemsPerPageChange,
      handleViewDetails,
      closeDetailsModal,
      handleRefresh,
    };
  },
  computed: {
    ...mapGetters(["getAuthenticatedUser"]),
    isSuperAdmin() {
      const permissions = this.getAuthenticatedUser?.admin_permissions;
      return Array.isArray(permissions) && permissions.includes("SUPER_ADMIN");
    },
  },
};
</script>

<style scoped>
.view-request-logs {
  display: grid;
  gap: 1rem;
  min-width: 0;
}

.view-request-logs__content {
  display: grid;
  gap: 1rem;
  min-width: 0;
}

.view-request-logs__summary {
  display: grid;
  gap: 0.85rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.view-request-logs__summary-item {
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 94%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  padding: 0.9rem;
}

.view-request-logs__summary-item span {
  color: var(--hs-muted, #64748b);
  display: block;
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.view-request-logs__summary-item strong {
  color: var(--hs-text, #0f172a);
  display: block;
  font-size: 1.15rem;
  font-weight: 950;
  margin-top: 0.25rem;
}

.view-request-logs__alert {
  margin-bottom: 1rem;
}

@media (max-width: 760px) {
  .view-request-logs__summary {
    grid-template-columns: 1fr;
  }
}
</style>
